import { AtlasRouteIds } from '@/components/navigation/constants';
import { FeatureFlagSet } from '@/types';

/**
 * Routes fully migrated from Darkwing; always served by Gaia (no LD gate).
 * Not used at runtime — discoverability for Phase 5 graduation status.
 */
export const MIGRATION_COMPLETE_ROUTES = [
  AtlasRouteIds.Alarms,
  AtlasRouteIds.CoincidentPeaks,
  AtlasRouteIds.CompressorSequencer,
  AtlasRouteIds.CondenserSequencer,
  AtlasRouteIds.DemandEvents,
  AtlasRouteIds.Equipment,
  AtlasRouteIds.FacilityPerformance,
  AtlasRouteIds.Map,
  AtlasRouteIds.Reports,
  AtlasRouteIds.System,
  AtlasRouteIds.Trends,
] as const satisfies readonly string[];

/**
 * Routes with a Darkwing counterpart. When the flag is:
 * - true: Gaia handles the feature
 * - false: Redirect to Darkwing
 *
 * Used by migration guards and BetaCompareButton.
 */
export const MIGRATION_ROUTE_FEATURE_FLAGS: Record<string, keyof FeatureFlagSet> = {
  [AtlasRouteIds.PredictiveDryingControl]: 'predictive-drying-control',
  [AtlasRouteIds.ProcessView]: 'gaia-process',
  [AtlasRouteIds.SensorCalibration]: 'gaia-sensor-calibration',
} as const;

/**
 * Gaia-only routes that never existed in Darkwing. When the flag is off,
 * createFeatureFlagGuard redirects to the facility default route (not Darkwing).
 */
export const GAIA_ONLY_ROUTE_FEATURE_FLAGS: Record<string, keyof FeatureFlagSet> = {
  [AtlasRouteIds.DemandResponseEvents]: 'demand-response-pages',
  [AtlasRouteIds.UxConfig]: 'ux-config',
  [AtlasRouteIds.Uxds]: 'uxds-ui',
} as const;

/**
 * Combined mapping of routes to their feature flags (migration + Gaia-only).
 * Used by route guards and left-nav flagRestricted visibility.
 */
export const ROUTE_FEATURE_FLAGS: Record<string, keyof FeatureFlagSet> = {
  ...MIGRATION_ROUTE_FEATURE_FLAGS,
  ...GAIA_ONLY_ROUTE_FEATURE_FLAGS,
} as const;

/**
 * Checks if a route has a feature flag configured (migration or Gaia-only).
 * @param value - The route identifier to check
 * @returns The feature flag key if configured, false otherwise
 */
export const hasRouteFeatureFlag = (value: string) =>
  (value && ROUTE_FEATURE_FLAGS[value]) ?? false;

/**
 * Checks if a route is a Darkwing-comparable migration route.
 * Gaia-only routes are excluded so BetaCompareButton does not open a missing Darkwing page.
 */
export const hasMigrationRouteFeatureFlag = (value: string) =>
  (value && MIGRATION_ROUTE_FEATURE_FLAGS[value]) ?? false;
